import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, Upload, Users, ArrowRight, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PlayerImportDialog } from "@/components/PlayerImportDialog";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useTranslation } from "@/hooks/useTranslation";

interface OnboardingPlayersProps {
  onNext: () => void;
  onBack: () => void;
  playersAdded: number;
  setPlayersAdded: (count: number) => void;
}

export default function OnboardingPlayers({
  onNext,
  onBack,
  playersAdded,
  setPlayersAdded,
}: OnboardingPlayersProps) {
  const { toast } = useToast();
  const { updateStep } = useOnboarding();
  const { translate } = useTranslation();
  const [teamId, setTeamId] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isAddingManually, setIsAddingManually] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    position: "",
    jersey_number: "",
  });

  useEffect(() => {
    loadTeamAndPlayers();
  }, []);

  const loadTeamAndPlayers = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: userRoles } = await supabase
        .from("user_organization_roles")
        .select("organization_id")
        .eq("user_id", session.user.id)
        .limit(1);

      if (userRoles && userRoles.length > 0) {
        setOrganizationId(userRoles[0].organization_id);

        const { data: teams } = await supabase
          .from("teams")
          .select("id")
          .eq("organization_id", userRoles[0].organization_id)
          .limit(1);

        if (teams && teams.length > 0) {
          setTeamId(teams[0].id);

          const { count } = await supabase
            .from("players")
            .select("*", { count: "exact", head: true })
            .eq("team_id", teams[0].id);

          setPlayersAdded(count || 0);
        }
      }
    } catch (error) {
      console.error("Error loading team:", error);
    }
  };

  const handleAddManualPlayer = async () => {
    if (!formData.first_name || !formData.last_name) {
      toast({
        title: translate("onboarding_missing_info"),
        description: translate("onboarding_name_required"),
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase.from("players").insert({
        team_id: teamId || null,
        organization_id: organizationId || null,
        first_name: formData.first_name,
        last_name: formData.last_name,
        position: formData.position || null,
        jersey_number: formData.jersey_number ? parseInt(formData.jersey_number) : null,
      });

      if (error) throw error;

      setPlayersAdded(playersAdded + 1);
      setFormData({ first_name: "", last_name: "", position: "", jersey_number: "" });
      setIsAddingManually(false);

      toast({
        title: translate("onboarding_player_added"),
        description: translate("onboarding_player_added_desc", { name: `${formData.first_name} ${formData.last_name}` }),
      });
    } catch (error: any) {
      toast({
        title: translate("onboarding_error_adding"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleNext = async () => {
    if (playersAdded > 0) {
      await updateStep("step_players", true);
      onNext();
    } else {
      toast({
        title: translate("onboarding_add_player_required"),
        description: translate("onboarding_add_player_required_desc"),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-4">
        <h2 className="text-3xl md:text-4xl font-bold">Add Your Players</h2>
        <p className="text-muted-foreground">
          Start by adding your squad roster. You can upload a CSV or add players manually.
        </p>
      </div>

      {playersAdded > 0 && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="flex items-center justify-center gap-3 py-6">
            <Users className="h-5 w-5 text-primary" />
            <span className="text-lg font-semibold">
              {playersAdded} player{playersAdded !== 1 ? "s" : ""} added
            </span>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="hover:border-primary/50 transition-colors cursor-pointer" onClick={() => setIsImportDialogOpen(true)}>
          <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Upload className="h-8 w-8 text-primary" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold text-lg">Upload CSV</h3>
              <p className="text-sm text-muted-foreground">Import your entire roster at once</p>
            </div>
            <Button className="w-full">Upload Roster</Button>
          </CardContent>
        </Card>

        <Card className="hover:border-primary/50 transition-colors cursor-pointer" onClick={() => setIsAddingManually(true)}>
          <CardContent className="flex flex-col items-center justify-center p-8 space-y-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <UserPlus className="h-8 w-8 text-primary" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold text-lg">Add Manually</h3>
              <p className="text-sm text-muted-foreground">Add players one at a time</p>
            </div>
            <Button variant="outline" className="w-full">Add Player</Button>
          </CardContent>
        </Card>
      </div>

      {isAddingManually && (
        <Card>
          <CardContent className="space-y-4 pt-6">
            <h3 className="font-semibold">Add Player Manually</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  placeholder="John"
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  placeholder="Smith"
                />
              </div>
              <div>
                <Label htmlFor="position">Position</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  placeholder="Forward"
                />
              </div>
              <div>
                <Label htmlFor="jersey_number">Jersey Number</Label>
                <Input
                  id="jersey_number"
                  type="number"
                  value={formData.jersey_number}
                  onChange={(e) => setFormData({ ...formData, jersey_number: e.target.value })}
                  placeholder="10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddManualPlayer}>Add Player</Button>
              <Button variant="outline" onClick={() => setIsAddingManually(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-between pt-8">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button onClick={handleNext} disabled={playersAdded === 0}>
          Continue
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      <PlayerImportDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        teamId={teamId}
        onImportComplete={loadTeamAndPlayers}
      />
    </div>
  );
}
